import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import injectSheet from 'react-jss';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import { Field } from 'redux-form';
import Section from '../../Section';
import Timer from '../../Timer';
import { getTextColorsFromSection } from '../../lib/colors';
import Typography from '../../Typography';
import PaymentFormInput from './PaymentFormInput';
import CreditCardFields from './CreditCardFields';
import OrderSummary from '../OrderSummary';
import FormToast from '../FormToast';
import AddressInput from '../AddressInput';
import { address } from '../../lib/validate';

function OrderPaymentForm({
  classes,
  event,
  site,
  section,
  ticketsCount,
  paymentForm,
  preOrder,
  timeOffset,
  handleSubmit,
  submitting,
  onPreOrderExpired,
  orderError,
  submitFailed,
  installmentsData,
  setInstallments,
  installment,
  error,
  foreignBuyer,
  cardNumber,
  change,
  ccv,
}) {
  const { styles } = section;
  const colors = getTextColorsFromSection(site, section);
  const bankSlipEnabled =
    !event.registerPaymentForms.bankSlip.hiddenNextEvent ||
    event.dateRange.start >
      moment()
        .endOf('day')
        .add(5, 'days')
        .toISOString();

  return (
    <Section site={site} section={section} className={classes.root} rootClassName={classes.root}>
      <form className={classes.form} onSubmit={handleSubmit}>
        <div className={classes.header}>
          <Typography
            type="title"
            className={classes.timer}
            style={{ ...colors.title, ...styles.title }}
          >
            <Timer to={preOrder.expireAt} offset={timeOffset} onEnd={onPreOrderExpired} />
          </Typography>
          <Typography
            type="caption"
            style={{ ...colors.title, ...styles.title }}
            className={classes.headerText}
          >
            <FormattedMessage id="preOrderExpirationMessage" />
          </Typography>
        </div>
        {((!submitting && submitFailed) || orderError) && (
          <FormToast className={classes.toast}>
            <FormattedMessage
              id={
                (orderError && orderError.networkError) || error
                  ? 'orderSubmissionError'
                  : submitFailed
                  ? 'orderValidationError'
                  : 'paymentError'
              }
            />
          </FormToast>
        )}
        <div className={classes.fields}>
          <Typography type="sectionTitle">
            <FormattedMessage id="paymentData" />
          </Typography>
          {preOrder.totalValue > 0 && (
            <Fragment>
              <div className={classes.box}>
                <Typography type="caption" gutterBottom>
                  <strong>
                    <FormattedMessage id={bankSlipEnabled ? 'paymentForm' : 'creditCard'} />
                  </strong>
                </Typography>
                {bankSlipEnabled && !foreignBuyer && <PaymentFormInput />}
                {paymentForm === 'creditCard' ? (
                  <CreditCardFields
                    installments={installmentsData && installmentsData.installments}
                    setInstallments={setInstallments}
                    cardNumber={cardNumber}
                    ccv={ccv}
                    onChange={change}
                  />
                ) : (
                  <Fragment>
                    <Typography type="caption" gutterBottom>
                      <FormattedMessage id="bankSlipHint" />
                    </Typography>
                    <Typography type="caption" paragraph>
                      <FormattedMessage id="bankSlipRegistrationWarning" />
                    </Typography>
                  </Fragment>
                )}
              </div>
              <Typography type="sectionTitle">
                <FormattedMessage id="billingAddress" />
              </Typography>
              <div className={classes.addressFieldWrapper}>
                <Field
                  component={AddressInput}
                  validate={address}
                  foreignBuyer={foreignBuyer}
                  name="buyer.address"
                />
              </div>
            </Fragment>
          )}
        </div>
        <OrderSummary
          event={event}
          site={site}
          section={section}
          ticketsCount={ticketsCount}
          subtotalValue={preOrder.subtotalValue}
          totalValue={
            paymentForm === 'creditCard' && installment
              ? installment.grandTotalValue
              : preOrder.totalValue
          }
          submitting={submitting}
          className={classes.summary}
          lastStep
        />
      </form>
    </Section>
  );
}

OrderPaymentForm.propTypes = {
  classes: PropTypes.object.isRequired,
  event: PropTypes.object.isRequired,
  site: PropTypes.object.isRequired,
  section: PropTypes.object.isRequired,
  ticketsCount: PropTypes.number.isRequired,
  preOrder: PropTypes.object.isRequired,
  timeOffset: PropTypes.number.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  onPreOrderExpired: PropTypes.func.isRequired,
  setInstallments: PropTypes.func.isRequired,
  paymentForm: PropTypes.string,
  foreignBuyer: PropTypes.bool,
  orderError: PropTypes.object,
  submitFailed: PropTypes.bool,
  installmentsData: PropTypes.object,
  installment: PropTypes.object,
  error: PropTypes.object,
  cardNumber: PropTypes.string,
  ccv: PropTypes.string,
};

OrderPaymentForm.defaultProps = {
  paymentForm: 'creditCard',
  foreignBuyer: false,
  installmentsData: null,
  installment: null,
  orderError: null,
  submitFailed: false,
  error: undefined,
  cardNumber: undefined,
  ccv: undefined,
};

const stylesheet = theme => ({
  root: {
    height: '100%',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: `calc(100vh - ${12 * theme.spacing.unit}px)`,
    height: '100%',
    [theme.breakpoints.down('sm')]: {
      maxHeight: '100%',
    },
  },
  header: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    gridArea: 'header',
    padding: [
      [theme.spacing.small, theme.spacing.xlarge, theme.spacing.small, theme.spacing.large],
    ],
    borderBottom: [[1, 'solid', theme.palette.common.faintBlack]],
    [theme.breakpoints.down('sm')]: {
      padding: [theme.spacing.unit, theme.spacing.xlarge, theme.spacing.unit, theme.spacing.small],
    },
  },
  toast: {
    gridArea: 'error',
  },
  timer: {
    flex: '0 0 auto',
    marginRight: theme.spacing.medium,
    [theme.breakpoints.down('sm')]: {
      marginRight: theme.spacing.small,
      fontSize: 14,
    },
  },
  headerText: {
    [theme.breakpoints.down('sm')]: {
      fontSize: 14,
    },
  },
  fields: {
    flex: '1 1 auto',
    padding: theme.spacing.large,
    overflow: 'auto',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing.small,
    },
  },
  box: {
    '&:not(:last-child)': {
      marginBottom: theme.spacing.medium,
    },
  },
  addressFieldWrapper: {
    position: 'relative',
  },
  summary: {
    gridArea: 'summary',
  },
});

export default injectSheet(stylesheet)(OrderPaymentForm);



// WEBPACK FOOTER //
// ./src/site-render/src/RegisterExtension/OrderPaymentForm/OrderPaymentForm.js